import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { AuthStore } from '@store/auth.store';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Apollo } from 'apollo-angular';
import { MAP_PARTNERS_BOOKINGS_QUERY } from '@gql_queries/realtime_map/queries';
import { MapPartnerBooking } from '@interfaces/models.interface';
import { greyMapStyle } from '@map_theme/grey';
import { Store } from '@ngrx/store';
import * as RtMapActions from '../../../../../redux/actions/rtmap.actions';

@Component({
  selector: 'app-rt-map-layout',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule, GoogleMap],
  templateUrl: './rt-map-layout.component.html',
  styleUrl: './rt-map-layout.component.css'
})
export class RtMapLayoutComponent {
  constructor(
    private apollo: Apollo,
    private authStore: AuthStore,
    private router: Router,
    private location: Location,
    private store: Store
  ) { }

  center: google.maps.LatLngLiteral = {
    lat: 14.6105,  // Eastwood Mall latitude
    lng: 121.0562  // Eastwood Mall longitude
  };
  zoom = 13;
  display: any;
  private refreshInterval: any;
  public mapData: MapPartnerBooking[] = [];
  public totalMarkers: number = 0;
  public activeMarkers: number = 0;
  public inactiveMarkers: number = 0;

  // Add map options for greyish style
  mapOptions: google.maps.MapOptions = greyMapStyle;

  // Replace both marker arrays with a single one
  public markers: {
    position: google.maps.LatLngLiteral;
    title: string;
    options: google.maps.MarkerOptions;
    partnerId: string;
    status: string;
    currentBooking: any;
    vehicleType: string;
  }[] = [];

  // Context menu state
  showContextMenu = false;
  contextMenuPosition = { x: 0, y: 0 };
  selectedMarker: any = null;

  baseHyperLinkUrlTrans = `${window.location.origin}/admin/transactions/?serviceType=<serviceType>&transId=<transId>`;

  // Handle marker click
  onMarkerClick(marker: any, event: google.maps.MapMouseEvent) {
    this.selectedMarker = marker;
    if (event.domEvent instanceof MouseEvent) {
      // Add offset to prevent tooltip from appearing directly under the cursor
      this.contextMenuPosition = {
        x: event.domEvent.clientX + 10,
        y: event.domEvent.clientY + 10
      };
      this.showContextMenu = true;
    }
  }

  // Handle map click to close tooltip
  onMapClick() {
    this.showContextMenu = false;
    this.selectedMarker = null;
  }

  // Handle context menu actions
  onContextMenuAction(action: string) {
    if (this.selectedMarker) {
      switch (action) {
        case 'viewDetails':
          console.log('View details for:', this.selectedMarker.partnerId);
          // Add your view details logic here
          break;
        case 'trackLocation':
          console.log('Track location for:', this.selectedMarker.partnerId);
          // Add your tracking logic here
          break;
        case 'sendMessage':
          console.log('Send message to:', this.selectedMarker.partnerId);
          // Add your messaging logic here
          break;
      }
    }
    this.showContextMenu = false;
  }

  getTransactionUrl(serviceType: string, transId: string): string {
    return this.baseHyperLinkUrlTrans
      .replace('<serviceType>', serviceType)
      .replace('<transId>', transId);
  }

  // Update markers based on mapData
  updateMarkers() {
    // Clear existing markers
    this.markers = [];

    // Process all map data
    this.mapData.forEach((data: MapPartnerBooking) => {
      // Determine status based on bookingRide and bookingExpress
      const status = (!data.bookingRide && !data.bookingExpress) ? 'Inactive' : 'Active';
      
      // Determine current booking
      const currentBooking = data.bookingExpress || data.bookingRide || null;
      
      // Add rideType if it doesn't exist or is empty
      if (currentBooking && (!('rideType' in currentBooking) || !(currentBooking as any).rideType)) {
        (currentBooking as any).rideType = 'express';
      }

      console.log(currentBooking);
      // Get vehicle type from partnerVehicles
      const vehicleType = data.vehicleType || 'Unknown';

      const markerData = {
        position: {
          lat: data.latitude,
          lng: data.longitude
        },
        title: `${data.firstName} ${data.lastName}`,
        partnerId: data.partnerId,
        status: status,
        isOkRide: data.partnerVehicles?.[0]?.isOkRide,
        isExpress: data.partnerVehicles?.[0]?.isExpress,
        isMototaxi: data.partnerVehicles?.[0]?.isMototaxi,
        currentBooking: currentBooking,
        hyperLinkUrlTrans: currentBooking ? this.getTransactionUrl(
          currentBooking?.rideType || '',
          'bookingRideId' in currentBooking ? currentBooking.bookingRideId : currentBooking?.bookingExpressId || ''
        ) : '',
        vehicleType: vehicleType,
        options: {
          icon: {
            path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            scale: 5,
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: '#FFFFFF',
            fillColor: '#ff005d',
            rotation: data.locationHeading
          }
        }
      };

      this.markers.push(markerData);
      
      // Update marker counts
      this.updateMarkerCounts();
    });
  }

  private updateMarkerCounts(): void {
    // Get total count
    const totalMarkers = this.markers.length;

    // Get active count
    const activeMarkers = this.markers.filter(marker => marker.status === 'Active').length;

    // Get inactive count
    const inactiveMarkers = this.markers.filter(marker => marker.status === 'Inactive').length;

    // Dispatch to Redux store
    this.store.dispatch(RtMapActions.loadPartnerStatsSuccess({
      totalPartners: totalMarkers,
      totalActivePartners: activeMarkers,
      totalInactivePartners: inactiveMarkers
    }));

    // Keep local state for component use
    /* this.totalMarkers = totalMarkers;
    this.activeMarkers = activeMarkers;
    this.inactiveMarkers = inactiveMarkers; */
  }

  fetchMapData() {
    // Dispatch loading action
    this.store.dispatch(RtMapActions.loadPartnerStats());

    this.apollo
      .watchQuery({
        fetchPolicy: 'network-only',
        query: MAP_PARTNERS_BOOKINGS_QUERY
      }).valueChanges.subscribe({
        next: (res: any) => {
          if(res.data['mapPartnerBookings']){
            this.mapData = res.data['mapPartnerBookings'];
            this.updateMarkers();
          }
        },
        error: (error) => {
          if (error.message === 'Unauthorized' || error?.networkError?.status === 403) {
            this.authStore.logout().subscribe(() => {
              this.router.navigate(['/admin/login']);
            });
          }
          // Dispatch error action
          this.store.dispatch(RtMapActions.loadPartnerStatsFailure({ error: error.message }));
        }
      });
  }

  moveMap(event: google.maps.MapMouseEvent) {
    if (event.latLng != null) this.center = (event.latLng.toJSON());
  }

  move(event: google.maps.MapMouseEvent) {
    if (event.latLng != null) this.display = event.latLng.toJSON();
  }

  ngOnInit() {
    
    this.fetchMapData();
    
    // Set up interval (30000ms = 30s)
    this.refreshInterval = setInterval(() => {
      this.fetchMapData();
    }, 30000);
  }

  ngOnDestroy() {
    // Clean up interval when component is destroyed
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
}